import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MonitorPlay, ChevronLeft, ChevronRight, Check,
  Minimize2, X, Loader2, AlertTriangle, Tv, Power, Info,
} from 'lucide-react';
import { albumsApi } from '../../api/albums';
import { eventsApi } from '../../api/events';
import { VenueDisplayPreview } from './VenueDisplayPreview';
import { ConnectDisplayDialog } from './ConnectDisplayDialog';
import { ModeDiagram, CollageDiagram, FitDiagram, TransitionDiagram, PortraitDiagram } from './SlideshowDiagrams';
import { DEFAULT_SLIDESHOW_CONFIG, RECOMMENDED_SLIDESHOW_CONFIG } from '../../types';
import { COLLAGE_SIZES, isCollageSlideFor } from '../../lib/slideshowPresentation';
import type {
  Album, Photo, SlideshowConfig, SlideshowTransition, SlideshowPhotoOrder,
  SlideshowMode, SlideshowCollageLayout, SlideshowPhotoFit,
} from '../../types';
import styles from './DisplayStudio.module.css';

/**
 * Display Studio — the workspace for configuring what guests see on the TV.
 *
 * Architecture: top bar (identity / status / connect), a stage that renders
 * the display at size, and an inspector that edits it. The stage is the
 * product; the inspector is its controls. Connection is a discrete task
 * behind a dialog rather than permanent furniture in the workspace.
 */

/**
 * Duration reads as a pace, not a number, and the guidance changes with the
 * choice rather than sitting there as static help text.
 */
const DURATION_OPTIONS: { value: number; label: string; note: string }[] = [
  { value: 3, label: '3s', note: 'Fast — good for very large albums.' },
  { value: 5, label: '5s', note: 'Quick pace — keeps things moving.' },
  { value: 8, label: '8s', note: 'A good balance for most events.' },
  { value: 10, label: '10s', note: 'Relaxed pace.' },
  { value: 15, label: '15s', note: 'Slower — good for showcasing individual photos.' },
];

const TRANSITIONS: { value: SlideshowTransition; label: string }[] = [
  { value: 'kenburns', label: 'Ken Burns' },
  { value: 'crossfade', label: 'Crossfade' },
  { value: 'slide', label: 'Slide' },
  { value: 'zoom', label: 'Zoom' },
  { value: 'fade-to-black', label: 'To black' },
];

const ORDERS: { value: SlideshowPhotoOrder; label: string }[] = [
  { value: 'random', label: 'Random' },
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'most_liked', label: 'Most liked' },
  { value: 'pinned_first', label: 'Pinned first' },
];

const LAYOUTS: { value: SlideshowMode; label: string; hint: string; recommended?: boolean }[] = [
  { value: 'single', label: 'Single', hint: 'One photo per slide', recommended: true },
  { value: 'collage', label: 'Collage', hint: 'Multiple photos per slide' },
  { value: 'mixed', label: 'Mixed', hint: 'Mostly single, occasional collage' },
];

const COLLAGE_LAYOUTS: { value: SlideshowCollageLayout; label: string }[] = [
  { value: '2-up', label: '2-up' },
  { value: '3-up', label: '3-up' },
  { value: '4-grid', label: '4-grid' },
  { value: 'mosaic', label: 'Mosaic' },
];

const COLLAGE_EVERY = [3, 5, 8, 10] as const;

/**
 * Stretch distorts faces and is essentially never the right answer for
 * photographs, so it is not offered alongside the two real choices.
 */
const FITS: { value: SlideshowPhotoFit; label: string; hint: string; recommended?: boolean }[] = [
  { value: 'fill', label: 'Fill', hint: 'Fills the screen. May crop edges.', recommended: true },
  { value: 'fit', label: 'Fit', hint: 'Whole photo. May leave space around it.' },
];

/** Order follows the user's mental model: what → how it looks → what it says → how it moves. */
const SECTIONS = [
  { id: 'content', label: 'Content' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'info', label: 'On-screen info' },
  { id: 'playback', label: 'Playback' },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];
type SheetId = SectionId;

const AUTOSAVE_DELAY_MS = 600;

const CONFIG_KEYS: (keyof SlideshowConfig)[] = [
  'intervalSeconds', 'transition', 'showPhotoCount', 'showAlbumName', 'showEventName',
  'photoOrder', 'mode', 'collageLayout', 'collageEveryN', 'photoFit', 'preservePortrait',
];

function configsEqual(a: SlideshowConfig, b: SlideshowConfig) {
  return CONFIG_KEYS.every((k) => a[k] === b[k]);
}

type SaveState = 'saved' | 'saving' | 'error';

// ── Primitives ───────────────────────────────────────────────────────────────

function Switch({
  checked, onChange, label, description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={styles['switch-row']}
    >
      <span className={styles['switch-text']}>
        <span className={styles['switch-label']}>{label}</span>
        <span className={styles['switch-desc']}>{description}</span>
      </span>
      <span className={[styles['switch'], checked ? styles['switch-on'] : ''].filter(Boolean).join(' ')}>
        <span className={styles['switch-knob']} />
      </span>
    </button>
  );
}

/** Segmented control with roving arrow-key selection. */
function Segmented<T extends string | number>({
  options, value, onChange, ariaLabel,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = options.findIndex((o) => o.value === value);
    const next = e.key === 'ArrowRight' ? (i + 1) % options.length : (i - 1 + options.length) % options.length;
    onChange(options[next].value);
  };

  return (
    <div className={styles['segmented']} role="radiogroup" aria-label={ariaLabel} onKeyDown={onKeyDown}>
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(o.value)}
            className={[styles['segment'], selected ? styles['segment-on'] : ''].filter(Boolean).join(' ')}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Tile<T extends string>({
  value, label, hint, diagram, selected, badge, onSelect,
}: {
  value: T;
  label: string;
  hint?: string;
  diagram: React.ReactNode;
  selected: boolean;
  badge?: string;
  onSelect: (v: T) => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={() => onSelect(value)}
      className={[styles['tile'], selected ? styles['tile-on'] : ''].filter(Boolean).join(' ')}
    >
      <span className={styles['tile-art']}>{diagram}</span>
      <span className={styles['tile-label']}>
        {label}
        {selected && <Check size={12} aria-hidden="true" />}
      </span>
      {badge && <span className={styles['tile-badge']}>{badge}</span>}
      {hint && <span className={styles['tile-hint']}>{hint}</span>}
    </button>
  );
}

function Field({
  label, children, helper, info,
}: {
  label: string;
  children: React.ReactNode;
  helper?: string;
  /** Explanation kept behind a disclosure rather than permanently occupying
   *  sidebar space. */
  info?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles['field']}>
      <span className={styles['field-head']}>
        <span className={styles['field-label']}>{label}</span>
        {info && (
          <button
            type="button"
            className={styles['info-btn']}
            aria-label={`What does ${label} do?`}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <Info size={13} />
          </button>
        )}
      </span>
      {info && open && <p className={styles['info-body']}>{info}</p>}
      {children}
      {helper && <span className={styles['field-helper']}>{helper}</span>}
    </div>
  );
}

function ConfirmDialog({
  open, title, body, confirmLabel, destructive, loading, onCancel, onConfirm,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  destructive?: boolean;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onCancel(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className={styles['confirm-backdrop']}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
        >
          <motion.div
            role="alertdialog" aria-modal="true" aria-label={title}
            className={styles['confirm']}
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
          >
            <h3 className={styles['confirm-title']}>{title}</h3>
            <p className={styles['confirm-body']}>{body}</p>
            <div className={styles['confirm-actions']}>
              <button type="button" className={styles['btn-ghost']} onClick={onCancel}>Cancel</button>
              <button
                type="button"
                className={destructive ? styles['btn-danger'] : styles['btn-primary']}
                onClick={onConfirm}
                disabled={loading}
              >
                {loading ? <Loader2 size={14} className={styles['spin']} /> : null}
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

// ── Studio ───────────────────────────────────────────────────────────────────

interface DisplayStudioProps {
  album: Album;
  onUpdated: (a: Album) => void;
  onClose?: () => void;
}

export function DisplayStudio({ album, onUpdated, onClose }: DisplayStudioProps) {
  const saved = useMemo(
    () => ({ ...DEFAULT_SLIDESHOW_CONFIG, ...(album.slideshowConfig ?? {}) }),
    [album.slideshowConfig],
  );

  const [config, setConfig] = useState<SlideshowConfig>(saved);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const savedRef = useRef<SlideshowConfig>(saved);
  const [retryNonce, setRetryNonce] = useState(0);

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [photosLoading, setPhotosLoading] = useState(true);
  const [eventName, setEventName] = useState('');

  const [index, setIndex] = useState(0);
  const [slideNumber, setSlideNumber] = useState(0);

  const [connectOpen, setConnectOpen] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [mobileSheet, setMobileSheet] = useState<SheetId | null>(null);
  const [showStretch, setShowStretch] = useState(false);

  const [displayCode, setDisplayCode] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(true);
  const [passwordUnavailable, setPasswordUnavailable] = useState(false);


  // Prefer the short, typeable form; fall back to the long token link only if
  // the code hasn't loaded yet.
  const slideshowUrl = displayCode
    ? `${window.location.origin}/tv/${displayCode}`
    : album.slideshowToken
      ? `${window.location.origin}/slideshow/${album._id}/${album.slideshowToken}`
      : null;

  // ── Data ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setPhotosLoading(true);
    albumsApi.listPhotos(album._id)
      .then((d) => { if (!cancelled) setPhotos(d.photos); })
      .catch(() => { if (!cancelled) setPhotos([]); })
      .finally(() => { if (!cancelled) setPhotosLoading(false); });
    return () => { cancelled = true; };
  }, [album._id]);

  useEffect(() => {
    if (!album.eventId) return;
    let cancelled = false;
    eventsApi.get(album.eventId)
      .then((e) => { if (!cancelled) setEventName(e.name ?? ''); })
      .catch(() => { /* preview simply omits the event name */ });
    return () => { cancelled = true; };
  }, [album.eventId]);

  useEffect(() => {
    let cancelled = false;
    albumsApi.getSlideshowLink(album._id)
      .then((r) => { if (!cancelled) setDisplayCode(r.code); })
      .catch(() => { /* falls back to the long link below */ });
    return () => { cancelled = true; };
  }, [album._id]);

  useEffect(() => {
    let cancelled = false;
    setPasswordLoading(true);
    albumsApi.getSlideshowPassword(album._id)
      .then((r) => {
        if (cancelled) return;
        setPassword(r.password);
        setPasswordUnavailable(r.password === null);
      })
      .catch(() => { if (!cancelled) setPasswordUnavailable(true); })
      .finally(() => { if (!cancelled) setPasswordLoading(false); });
    return () => { cancelled = true; };
  }, [album._id]);

  // ── Autosave ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (configsEqual(config, savedRef.current)) return;
    setSaveState('saving');
    const t = setTimeout(async () => {
      try {
        const res = await albumsApi.updateSlideshowConfig(album._id, config);
        const persisted = { ...DEFAULT_SLIDESHOW_CONFIG, ...res.slideshowConfig };
        savedRef.current = persisted;
        onUpdated({ ...album, slideshowConfig: persisted });
        setSaveState('saved');
      } catch {
        setSaveState('error');
      }
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(t);
    // album/onUpdated excluded: onUpdated re-renders the parent with a new
    // album object, which would otherwise retrigger the save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, album._id, retryNonce]);

  // ── Stage cursor ────────────────────────────────────────────────────────
  const slideSize = isCollageSlideFor(config, slideNumber)
    ? Math.max(1, Math.min(COLLAGE_SIZES[config.collageLayout] ?? 4, photos.length || 1))
    : 1;

  const advance = useCallback((dir: 1 | -1) => {
    if (photos.length === 0) return;
    setSlideNumber((n) => Math.max(0, n + dir));
    setIndex((i) => ((i + dir * slideSize) % photos.length + photos.length) % photos.length);
  }, [photos.length, slideSize]);

  // Restart the stage when the layout mode changes under the viewer.
  useEffect(() => { setIndex(0); setSlideNumber(0); }, [config.mode]);

  // ── Keyboard ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  useEffect(() => {
    if (!mobileSheet) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setMobileSheet(null); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileSheet]);

  const handleDisable = async () => {
    setDisabling(true);
    try {
      await albumsApi.disableSlideshow(album._id);
      onUpdated({ ...album, slideshowEnabled: false, slideshowToken: null });
      setConfirmDisable(false);
    } finally {
      setDisabling(false);
    }
  };

  const summary = [
    LAYOUTS.find((l) => l.value === config.mode)?.label,
    FITS.find((f) => f.value === config.photoFit)?.label ?? 'Stretch',
    `${config.intervalSeconds}s`,
    TRANSITIONS.find((t) => t.value === config.transition)?.label,
  ].filter(Boolean).join(' · ');

  // ── Inspector body (shared by desktop panel and mobile sheets) ───────────
  const renderSection = (id: SheetId) => {
    if (id === 'content') {
      return (
        <>
          <Field label="Layout">
            <div className={styles['tiles-3']} role="radiogroup" aria-label="Layout">
              {LAYOUTS.map((l) => (
                <Tile
                  key={l.value}
                  value={l.value}
                  label={l.label}
                  hint={l.hint}
                  diagram={<ModeDiagram mode={l.value} />}
                  selected={config.mode === l.value}
                  badge={l.recommended ? 'Recommended' : undefined}
                  onSelect={(v) => setConfig((c) => ({ ...c, mode: v }))}
                />
              ))}
            </div>
          </Field>

          {config.mode !== 'single' && (
            <>
              <Field label="Collage style">
                <div className={styles['tiles-4']} role="radiogroup" aria-label="Collage style">
                  {COLLAGE_LAYOUTS.map((l) => (
                    <Tile
                      key={l.value}
                      value={l.value}
                      label={l.label}
                      diagram={<CollageDiagram layout={l.value} />}
                      selected={config.collageLayout === l.value}
                      onSelect={(v) => setConfig((c) => ({ ...c, collageLayout: v }))}
                    />
                  ))}
                </div>
              </Field>
              {config.mode === 'mixed' && (
                <Field label="Collage every">
                  <Segmented
                    ariaLabel="Collage frequency"
                    value={config.collageEveryN}
                    onChange={(v) => setConfig((c) => ({ ...c, collageEveryN: v }))}
                    options={COLLAGE_EVERY.map((n) => ({ value: n, label: `${n}` }))}
                  />
                </Field>
              )}
            </>
          )}

          <Field label="Photo order">
            <select
              className={styles['select']}
              aria-label="Photo order"
              value={config.photoOrder}
              onChange={(e) => setConfig((c) => ({ ...c, photoOrder: e.target.value as SlideshowPhotoOrder }))}
            >
              {ORDERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </>
      );
    }

    if (id === 'appearance') {
      return (
        <>
          <Field
            label="Screen fit"
            info="Fill makes the photo cover the whole TV — some edges may be cropped. Fit shows the complete photo, which can leave space around it."
          >
            <div className={styles['tiles-2']} role="radiogroup" aria-label="Screen fit">
              {FITS.map((f) => (
                <Tile
                  key={f.value}
                  value={f.value}
                  label={f.label}
                  hint={f.hint}
                  diagram={<FitDiagram fit={f.value} />}
                  selected={config.photoFit === f.value}
                  badge={f.recommended ? 'Recommended' : undefined}
                  onSelect={(v) => setConfig((c) => ({ ...c, photoFit: v }))}
                />
              ))}
            </div>

            {(showStretch || config.photoFit === 'stretch') ? (
              <div className={styles['tiles-2']} role="radiogroup" aria-label="More screen fit options">
                <Tile
                  value={'stretch' as SlideshowPhotoFit}
                  label="Stretch"
                  hint="Fills exactly — distorts the photo"
                  diagram={<FitDiagram fit="stretch" />}
                  selected={config.photoFit === 'stretch'}
                  onSelect={(v) => setConfig((c) => ({ ...c, photoFit: v }))}
                />
              </div>
            ) : (
              <button type="button" className={styles['more-link']} onClick={() => setShowStretch(true)}>
                More options
              </button>
            )}
          </Field>

          <Field label="Portrait photos">
            <button
              type="button"
              role="switch"
              aria-checked={config.preservePortrait}
              onClick={() => setConfig((c) => ({ ...c, preservePortrait: !c.preservePortrait }))}
              className={styles['portrait-row']}
            >
              <span className={styles['portrait-art']}>
                <PortraitDiagram whole={config.preservePortrait} />
              </span>
              <span className={styles['switch-text']}>
                <span className={styles['switch-label']}>Keep vertical photos whole</span>
                <span className={styles['switch-desc']}>
                  Show the complete portrait photo, with space around it when needed.
                </span>
              </span>
              <span className={[styles['switch'], config.preservePortrait ? styles['switch-on'] : ''].filter(Boolean).join(' ')}>
                <span className={styles['switch-knob']} />
              </span>
            </button>
          </Field>
        </>
      );
    }

    if (id === 'info') {
      return (
        <>
          <Switch
            checked={config.showAlbumName}
            onChange={(v) => setConfig((c) => ({ ...c, showAlbumName: v }))}
            label="Album name"
            description="Shows the album name on screen"
          />
          <Switch
            checked={config.showEventName}
            onChange={(v) => setConfig((c) => ({ ...c, showEventName: v }))}
            label="Event name"
            description="Shows the event name on screen"
          />
          <Switch
            checked={config.showPhotoCount}
            onChange={(v) => setConfig((c) => ({ ...c, showPhotoCount: v }))}
            label="Photo number"
            description="Shows which photo is showing, e.g. 9 / 50"
          />
        </>
      );
    }

    const durationNote = DURATION_OPTIONS.find((d) => d.value === config.intervalSeconds)?.note ?? '';
    return (
      <>
        <Field label="Time per photo" helper={durationNote}>
          <Segmented
            ariaLabel="Time per photo"
            value={config.intervalSeconds}
            onChange={(v) => setConfig((c) => ({ ...c, intervalSeconds: v }))}
            options={DURATION_OPTIONS.map((d) => ({ value: d.value, label: d.label }))}
          />
        </Field>

        <Field label="Transition">
          <div className={styles['tiles-3']} role="radiogroup" aria-label="Transition">
            {TRANSITIONS.map((t) => (
              <Tile
                key={t.value}
                value={t.value}
                label={t.label}
                diagram={<TransitionDiagram transition={t.value} />}
                selected={config.transition === t.value}
                onSelect={(v) => setConfig((c) => ({ ...c, transition: v }))}
              />
            ))}
          </div>
        </Field>
      </>
    );
  };

  const sectionMeta: Record<SheetId, { title: string; subtitle: string }> = {
    content: { title: 'Content', subtitle: 'What appears on screen.' },
    appearance: { title: 'Appearance', subtitle: 'How photos fill the screen.' },
    info: { title: 'On-screen information', subtitle: 'What viewers see over each photo.' },
    playback: { title: 'Playback', subtitle: 'How the slideshow moves.' },
  };

  return (
    <div className={[styles['studio'], onClose ? styles['studio-standalone'] : ''].filter(Boolean).join(' ')}>
      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <header className={styles['topbar']}>
        <div className={styles['topbar-left']}>
          <span className={styles['identity-icon']} aria-hidden="true"><MonitorPlay size={18} /></span>
          <div className={styles['identity']}>
            <span className={styles['identity-name']}>{album.name}</span>
            <span className={styles['identity-meta']}>
              {eventName && <span className={styles['identity-event']}>{eventName}</span>}
              {eventName && <span className={styles['dot-sep']}>·</span>}
              <span className={styles['live']}>
                <span className={styles['live-dot']} aria-hidden="true" />
                Live
              </span>
            </span>
          </div>
        </div>

        <div className={styles['topbar-right']}>
          <SaveStatus state={saveState} onRetry={() => setRetryNonce((n) => n + 1)} />

          {/* Single control for the preview mode — it toggles, so there is
              always a visible way back out of fullscreen. */}
          <button
            type="button"
            className={[styles['btn-secondary'], fullscreen ? styles['btn-secondary-active'] : ''].filter(Boolean).join(' ')}
            onClick={() => setFullscreen((v) => !v)}
            aria-pressed={fullscreen}
          >
            {fullscreen ? <Minimize2 size={16} /> : <Tv size={16} />}
            <span className={styles['btn-text']}>{fullscreen ? 'Exit preview' : 'Preview TV'}</span>
          </button>

          <button type="button" className={styles['btn-primary']} onClick={() => setConnectOpen(true)}>
            Connect
          </button>

          <span className={styles['topbar-divider']} aria-hidden="true" />

          <button
            type="button"
            className={styles['btn-danger-ghost']}
            onClick={() => setConfirmDisable(true)}
          >
            <Power size={16} />
            <span className={styles['btn-text']}>Disable</span>
          </button>

          {onClose && (
            <button type="button" className={styles['icon-btn']} onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          )}
        </div>
      </header>

      {/* ── Workspace ────────────────────────────────────────────────────── */}
      <div className={styles['workspace']}>
        <main className={[styles['stage'], fullscreen ? styles['stage-full'] : ''].filter(Boolean).join(' ')}>
          <p className={styles['stage-label']}>Live display</p>

          <div className={styles['stage-frame']}>
            <VenueDisplayPreview
              photos={photos}
              config={config}
              albumName={album.name}
              eventName={eventName}
              loading={photosLoading}
              index={index}
              slideNumber={slideNumber}
              onAdvance={advance}
              onAddPhotos={onClose}
            />
          </div>

          <div className={styles['stage-controls']}>
            <button type="button" className={styles['nav-btn']} onClick={() => advance(-1)}
              disabled={photos.length === 0} aria-label="Previous photo">
              <ChevronLeft size={16} />
            </button>
            <span className={styles['counter']}>
              {photos.length === 0 ? '0 / 0' : `${Math.min(index + 1, photos.length)} / ${photos.length}`}
            </span>
            <button type="button" className={styles['nav-btn']} onClick={() => advance(1)}
              disabled={photos.length === 0} aria-label="Next photo">
              <ChevronRight size={16} />
            </button>
          </div>

          <p className={styles['live-preview-note']}>
            <span className={styles['live-dot']} aria-hidden="true" />
            Live preview
          </p>

        </main>

        {/* ── Inspector ──────────────────────────────────────────────────── */}
        {!fullscreen && (
          <aside className={styles['inspector']} aria-label="Display settings">
            <div className={styles['inspector-head']}>
              <p className={styles['mini-summary']}>{summary}</p>
            </div>

            <div className={styles['inspector-body']}>
              {SECTIONS.map((sec) => (
                <section key={sec.id} id={`section-${sec.id}`} className={styles['section']}>
                  <div className={styles['section-head']}>
                    <h3 className={styles['section-title']}>{sectionMeta[sec.id].title}</h3>
                    <p className={styles['section-sub']}>{sectionMeta[sec.id].subtitle}</p>
                  </div>
                  <div className={styles['section-body']}>{renderSection(sec.id)}</div>
                </section>
              ))}

              <div className={styles['inspector-foot']}>
                <button type="button" className={styles['reset-link']} onClick={() => setConfirmReset(true)}>
                  Reset settings
                </button>
              </div>
            </div>
          </aside>
        )}
      </div>

      {/* ── Mobile section launcher + sheets ─────────────────────────────── */}
      {!fullscreen && (
        <nav className={styles['mobile-nav']} aria-label="Display settings">
          {SECTIONS.map((sec) => (
            <button key={sec.id} type="button" className={styles['mobile-nav-btn']} onClick={() => setMobileSheet(sec.id)}>
              {sec.label}
            </button>
          ))}
        </nav>
      )}

      {createPortal(
        <AnimatePresence>
        {mobileSheet && (
          <motion.div
            className={styles['sheet-backdrop']}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={(e) => { if (e.target === e.currentTarget) setMobileSheet(null); }}
          >
            <motion.div
              className={styles['sheet']}
              role="dialog" aria-modal="true" aria-label={sectionMeta[mobileSheet].title}
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 380, damping: 34 }}
            >
              <div className={styles['sheet-head']}>
                <div>
                  <h3 className={styles['section-title']}>{sectionMeta[mobileSheet].title}</h3>
                  <p className={styles['section-sub']}>{sectionMeta[mobileSheet].subtitle}</p>
                </div>
                <button type="button" className={styles['icon-btn']} onClick={() => setMobileSheet(null)} aria-label="Close">
                  <X size={18} />
                </button>
              </div>
              <div className={styles['sheet-body']}>{renderSection(mobileSheet)}</div>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>,
        document.body,
      )}

      <ConnectDisplayDialog
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        slideshowUrl={slideshowUrl}
        displayCode={displayCode}
        password={password}
        passwordLoading={passwordLoading}
        passwordUnavailable={passwordUnavailable}
        onChangePassword={() => { /* reset handled from album settings */ }}
      />

      <ConfirmDialog
        open={confirmReset}
        title="Reset display settings?"
        body="Your custom display preferences will be replaced with the recommended setup."
        confirmLabel="Reset"
        onCancel={() => setConfirmReset(false)}
        onConfirm={() => { setConfig({ ...RECOMMENDED_SLIDESHOW_CONFIG }); setConfirmReset(false); }}
      />

      <ConfirmDialog
        open={confirmDisable}
        title="Disable this display?"
        body="The TV will stop showing this slideshow. Your photos, album and settings will remain unchanged."
        confirmLabel="Disable display"
        destructive
        loading={disabling}
        onCancel={() => setConfirmDisable(false)}
        onConfirm={handleDisable}
      />
    </div>
  );
}

function SaveStatus({ state, onRetry }: { state: SaveState; onRetry: () => void }) {
  if (state === 'saving') {
    return <span className={styles['save']}><Loader2 size={13} className={styles['spin']} />Saving…</span>;
  }
  if (state === 'error') {
    return (
      <span className={styles['save-error']} role="alert">
        <AlertTriangle size={13} />
        Couldn&rsquo;t save changes
        <button type="button" className={styles['save-retry']} onClick={onRetry}>Retry</button>
      </span>
    );
  }
  return <span className={styles['save']} role="status">Saved</span>;
}
